import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { SiLinkedin } from "react-icons/si";
import { Mail, Phone, MapPin, ExternalLink, ArrowUp } from "lucide-react";

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const linkedinPage = "https://www.linkedin.com/company/survey-foods";
  const logoURL = "https://www.surveyfoods.com/public/assets/logos/orange_bg_logo.jpeg";

  const handleSignInClick = () => {
    window.open('https://app.surveyfoods.com', '_blank');
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const contactInfo = [
    {
      icon: Phone,
      label: t('footer.callUs'),
      value: "+51 999 413 975",
      href: "tel:+51999413975"
    },
    {
      icon: Mail,
      label: t('footer.administration'),
      value: "administracion@surveyfoods.com",
      href: "mailto:administracion@surveyfoods.com"
    },
    {
      icon: Mail,
      label: t('footer.generalInquiries'),
      value: "welcome@surveyfoods.com",
      href: "mailto:welcome@surveyfoods.com"
    }
  ];

  const quickLinks = [
    { name: t('navbar.signIn'), action: handleSignInClick, external: true },
    { name: t('navbar.contactUs'), action: () => document.querySelector('[data-contact]')?.scrollIntoView({ behavior: 'smooth' }), external: false },
    { name: t('footer.locations'), action: () => document.querySelector('[data-locations]')?.scrollIntoView({ behavior: 'smooth' }), external: false },
    { name: t('footer.services'), action: () => document.querySelector('[data-services]')?.scrollIntoView({ behavior: 'smooth' }), external: false }
  ];

  return (
    <footer className="bg-gradient-to-br from-[#2D2D2D] via-[#1a1a1a] to-[#2D2D2D] text-gray-300">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex flex-col items-center md:items-start mb-6">
              <img 
                src={logoURL} 
                alt="Survey Foods" 
                className="h-16 md:h-20 w-auto mb-4 hover:opacity-90 transition-opacity duration-300" 
              />
              <p className="text-gray-400 text-center md:text-left leading-relaxed max-w-md">
                {t('footer.companyDescription')}
              </p>
            </div>
            
            {/* Social Links */}
            <div className="flex justify-center md:justify-start space-x-4">
              <a 
                href={linkedinPage} 
                target="_blank" 
                rel="noreferrer" 
                className="group p-3 bg-gray-800 hover:bg-[#f84831] rounded-lg transition-all duration-300 transform hover:scale-110"
              >
                <SiLinkedin className="w-5 h-5 text-gray-300 group-hover:text-white transition-colors duration-300" />
              </a>
              <button
                onClick={handleSignInClick}
                className="group p-3 bg-gray-800 hover:bg-[#f84831] rounded-lg transition-all duration-300 transform hover:scale-110"
              >
                <ExternalLink className="w-5 h-5 text-gray-300 group-hover:text-white transition-colors duration-300" />
              </button>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-6">{t('footer.contactInfo')}</h3>
            <div className="space-y-4">
              {contactInfo.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group"
                >
                  {item.href ? (
                    <a 
                      href={item.href}
                      className="flex items-start space-x-3 text-gray-400 hover:text-[#f84831] transition-colors duration-300"
                    >
                      <item.icon className="w-5 h-5 mt-0.5 flex-shrink-0 group-hover:text-[#f84831] transition-colors duration-300" />
                      <div>
                        <p className="text-sm text-gray-500">{item.label}</p>
                        <p className="text-sm">{item.value}</p>
                      </div>
                    </a>
                  ) : (
                    <div className="flex items-start space-x-3 text-gray-400">
                      <item.icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-500">{item.label}</p>
                        <p className="text-sm">{item.value}</p>
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-6">{t('footer.quickLinks')}</h3>
            <div className="space-y-3">
              {quickLinks.map((link, index) => (
                <motion.button
                  key={index}
                  onClick={link.action}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group flex items-center text-gray-400 hover:text-[#f84831] transition-all duration-300 text-left"
                >
                  <span className="group-hover:translate-x-1 transition-transform duration-300">
                    {link.name}
                  </span>
                  {link.external && (
                    <ExternalLink className="w-3 h-3 ml-2 opacity-60 group-hover:opacity-100 transition-opacity duration-300" />
                  )}
                </motion.button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-center md:text-left">
              <p className="text-gray-500 text-sm">
                © {new Date().getFullYear()} {t('footer.copyright')}
              </p>
              <p className="text-gray-600 text-xs mt-1">
                {t('footer.tagline')}
              </p>
            </div>
            
            {/* Back to Top Button */}
            <button
              onClick={scrollToTop}
              className="group flex items-center space-x-2 text-gray-400 hover:text-[#f84831] transition-all duration-300"
            >
              <span className="text-sm">{t('footer.backToTop')}</span>
              <div className="p-2 bg-gray-800 group-hover:bg-[#f84831] rounded-lg transition-all duration-300 transform group-hover:scale-110">
                <ArrowUp className="w-4 h-4 group-hover:text-white transition-colors duration-300" />
              </div>
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;